import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronRight,
  ChevronsUpDown,
  Wrench,
  Copy,
  Check,
  Info,
  X,
  FileSpreadsheet,
  FileCode,
  SlidersHorizontal,
  Layers,
  Database,
  Sparkles,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { CustomDropdown } from '../../../components/CustomDropdown ';

export interface ConfigItem {
  db_type?: string;
  name: string;
  setting: any;
  unit?: string;
  source?: string;
  short_desc?: string;
  category?: string;
  collected_at?: string;
}

interface ConfigurationViewProps {
  activeCluster: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

// Smart categorization helper for Oracle parameters
const getSmartCategory = (item: ConfigItem, isOracle: boolean): string => {
  if (item.category && item.category !== 'Uncategorized' && item.category.trim() !== '') {
    return item.category;
  }
  if (!isOracle) {
    return item.category || 'General';
  }

  const name = (item.name || '').toLowerCase().replace(/^_/, '');

  if (
    name.startsWith('sga_') ||
    name.startsWith('pga_') ||
    name.startsWith('memory_') ||
    name.startsWith('shared_pool_') ||
    name.startsWith('db_cache_') ||
    name.startsWith('db_block_') ||
    name.startsWith('buffer_pool_') ||
    name.startsWith('large_pool_') ||
    name.startsWith('java_pool_') ||
    name.startsWith('streams_pool_') ||
    name.startsWith('inmemory_') ||
    name.startsWith('vector_memory_') ||
    name.startsWith('memoptimize_') ||
    name.startsWith('sort_area_') ||
    name.startsWith('hash_area_') ||
    name.startsWith('bitmap_') ||
    name.startsWith('create_bitmap_') ||
    name.startsWith('data_transfer_cache_') ||
    name.startsWith('olap_page_pool_') ||
    name.includes('lock_sga') ||
    name.includes('pre_page_sga') ||
    name.includes('shared_memory_address') ||
    name.startsWith('object_cache_')
  ) {
    return 'Memory & Cache';
  }

  if (
    name.startsWith('optimizer_') ||
    name.startsWith('cursor_') ||
    name.startsWith('query_rewrite_') ||
    name.startsWith('star_transformation_') ||
    name.startsWith('approx_') ||
    name.startsWith('group_by_') ||
    name.startsWith('sqltune_') ||
    name.startsWith('sql_') ||
    name.startsWith('plsql_') ||
    name.startsWith('plscope_') ||
    name.startsWith('result_cache_') ||
    name.startsWith('workarea_') ||
    name === 'open_cursors'
  ) {
    return 'Optimizer & SQL';
  }

  if (
    name.startsWith('audit_') ||
    name.startsWith('unified_audit_') ||
    name.startsWith('sec_') ||
    name.startsWith('sql92_security') ||
    name.includes('remote_os_roles') ||
    name.includes('remote_login_passwordfile') ||
    name.includes('os_roles') ||
    name.includes('allow_weak_crypto') ||
    name.includes('allow_group_access_to_sga') ||
    name.includes('allow_legacy_reco_protocol') ||
    name.includes('allow_global_dblinks') ||
    name.includes('allow_rowid_column_type') ||
    name.startsWith('pkcs11_') ||
    name.startsWith('pki_') ||
    name.startsWith('mfa_') ||
    name.startsWith('encrypt_') ||
    name.startsWith('tde_') ||
    name.includes('dbfips') ||
    name.startsWith('wallet_') ||
    name.startsWith('ldap_') ||
    name.startsWith('identity_provider_') ||
    name.startsWith('os_authent_')
  ) {
    return 'Security & Audit';
  }

  if (
    name.startsWith('log_') ||
    name.startsWith('archive_') ||
    name.startsWith('dg_broker_') ||
    name.startsWith('data_guard_') ||
    name.startsWith('standby_') ||
    name.startsWith('fal_') ||
    name.startsWith('redo_') ||
    name.startsWith('fast_start_') ||
    name.startsWith('adg_')
  ) {
    return 'Redo, Archive & Standby';
  }

  if (
    name.startsWith('db_file_') ||
    name.startsWith('db_create_') ||
    name.startsWith('db_recovery_') ||
    name.startsWith('db_flashback_') ||
    name.startsWith('db_flash_') ||
    name.startsWith('control_file') ||
    name.startsWith('control_files') ||
    name.startsWith('disk_') ||
    name.startsWith('filesystemio_') ||
    name.startsWith('asm_') ||
    name.startsWith('rman_') ||
    name.startsWith('pdc_file_') ||
    name.startsWith('temp_undo_') ||
    name.startsWith('undo_') ||
    name.includes('rollback_segment') ||
    name.startsWith('clonedb') ||
    name.startsWith('deferred_segment_creation') ||
    name.startsWith('file_mapping') ||
    name.startsWith('fileio_') ||
    name.startsWith('instant_restore')
  ) {
    return 'Storage, Undo & Recovery';
  }

  if (
    name.startsWith('parallel_') ||
    name.includes('recovery_parallelism') ||
    name.includes('containers_parallel_degree') ||
    name.includes('dml_locks') ||
    name.includes('distributed_lock_timeout') ||
    name.includes('ddl_lock_timeout') ||
    name.includes('lockfree_reservation') ||
    name.includes('paranoid_concurrency_mode') ||
    name.startsWith('priority_txns_') ||
    name.startsWith('txn_')
  ) {
    return 'Parallelism & Transactions';
  }

  if (
    name.startsWith('pdb_') ||
    name.includes('pluggable_database') ||
    name.includes('autotask_max_active_pdbs') ||
    name.startsWith('awr_pdb_') ||
    name.startsWith('cdb_') ||
    name.includes('target_pdbs') ||
    name.includes('max_pdbs') ||
    name.startsWith('containers_') ||
    name.startsWith('container_data') ||
    name.startsWith('auto_start_pdb_services') ||
    name.startsWith('hybrid_read_only') ||
    name.startsWith('noncdb_compatible') ||
    name.startsWith('common_user_prefix') ||
    name.startsWith('dbnest_')
  ) {
    return 'Multitenant (CDB/PDB)';
  }

  if (
    name.includes('dispatcher') ||
    name.includes('listener') ||
    name.startsWith('connection_brokers') ||
    name.startsWith('service_names') ||
    name.startsWith('shared_server') ||
    name.startsWith('circuits') ||
    name.startsWith('drcp_') ||
    name.startsWith('use_dedicated_broker') ||
    name.startsWith('client_prefetch_rows') ||
    name.startsWith('client_result_cache_') ||
    name.startsWith('client_statistics_level') ||
    name.startsWith('outbound_dblink_protocols') ||
    name.startsWith('open_links')
  ) {
    return 'Network & Services';
  }

  if (
    name.startsWith('nls_') ||
    name.startsWith('timezone_') ||
    name.startsWith('sysdate_') ||
    name.startsWith('time_') ||
    name.startsWith('calendar_')
  ) {
    return 'NLS & Globalization';
  }

  if (
    name.startsWith('diagnostic_') ||
    name.startsWith('trace_') ||
    name.startsWith('tracefile_') ||
    name.startsWith('core_dump_') ||
    name.startsWith('shadow_core_dump') ||
    name.startsWith('background_core_dump') ||
    name.startsWith('background_dump_dest') ||
    name.startsWith('user_dump_dest') ||
    name.startsWith('alert_log_') ||
    name.startsWith('debug_log_') ||
    name.startsWith('max_dump_file_size') ||
    name.startsWith('diagnostics_control') ||
    name.startsWith('error_message_details') ||
    name.startsWith('run_addm_for_awr_report') ||
    name.startsWith('ipddb_enable') ||
    name === 'event'
  ) {
    return 'Diagnostics & Tracing';
  }

  if (
    name.startsWith('job_queue_') ||
    name.startsWith('scheduler_') ||
    name.startsWith('autotask_') ||
    name.startsWith('aq_') ||
    name.startsWith('saga_')
  ) {
    return 'Scheduler & AQ';
  }

  if (
    name.startsWith('xml_') ||
    name.startsWith('json_') ||
    name.startsWith('soda_') ||
    name.startsWith('kafka_') ||
    name.startsWith('datalake_') ||
    name.startsWith('mle_')
  ) {
    return 'XML, JSON & Features';
  }

  if (
    name.startsWith('processes') ||
    name.startsWith('sessions') ||
    name.startsWith('cpu_') ||
    name.startsWith('license_') ||
    name.startsWith('max_idle_') ||
    name.startsWith('resource_')
  ) {
    return 'System Limits & Resources';
  }

  return 'General & System';
};

export const ConfigurationView: React.FC<ConfigurationViewProps> = ({
  activeCluster,
  clientId,
  refreshKey,
  dbType,
}) => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<ConfigItem[]>([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'custom' | 'default'>('all');
  const sourceOptions = useMemo(
    () => [
      { label: 'All Sources', value: 'all' },
      { label: 'Custom / Modified Only', value: 'custom' },
      { label: 'Default Only', value: 'default' },
    ],
    []
  );
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<ConfigItem | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const isOracle = useMemo(() => {
    if (dbType?.toLowerCase() === 'oracle') return true;
    return settings.length > 0 && settings[0]?.db_type?.toLowerCase() === 'oracle';
  }, [dbType, settings]);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);
    try {
      const res = await baseapi.get('/db_monitor_dashboard/configuration', {
        params: { cluster_id: activeCluster, client_id: clientId },
      });
      const data = res.data?.data || res.data;
      const settingsList = data?.settings || (Array.isArray(data) ? data : []);
      setSettings(settingsList);
    } catch (err) {
      console.warn('ConfigurationView fetch error:', err);
      setSettings([]);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Enhanced settings with computed category and source normalized
  const enrichedSettings = useMemo(() => {
    return settings.map((item) => {
      const cat = getSmartCategory(item, isOracle);
      const isCustom = !item.source || item.source.toLowerCase() !== 'default';
      return {
        ...item,
        computedCategory: cat,
        isCustom,
      };
    });
  }, [settings, isOracle]);

  // Category list with counts
  const categoryStats = useMemo(() => {
    const map = new Map<string, number>();
    enrichedSettings.forEach((item) => {
      const cat = item.computedCategory;
      map.set(cat, (map.get(cat) || 0) + 1);
    });

    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [enrichedSettings]);

  // Quick summary counts
  const summaryCounts = useMemo(() => {
    const total = enrichedSettings.length;
    const custom = enrichedSettings.filter((s) => s.isCustom).length;
    const defaultCount = total - custom;
    return { total, custom, defaultCount };
  }, [enrichedSettings]);

  // Filter settings based on search, selected category, and source
  const filtered = useMemo(() => {
    return enrichedSettings.filter((s) => {
      // Category filter
      if (selectedCategory !== 'all' && s.computedCategory !== selectedCategory) {
        return false;
      }

      // Source filter
      if (sourceFilter === 'custom' && !s.isCustom) return false;
      if (sourceFilter === 'default' && s.isCustom) return false;

      // Text search
      if (!search.trim()) return true;
      const query = search.toLowerCase();
      const valStr = s.setting !== undefined && s.setting !== null ? String(s.setting).toLowerCase() : '';
      return (
        (s.name || '').toLowerCase().includes(query) ||
        (s.short_desc || '').toLowerCase().includes(query) ||
        valStr.includes(query) ||
        s.computedCategory.toLowerCase().includes(query) ||
        (s.source || '').toLowerCase().includes(query)
      );
    });
  }, [enrichedSettings, selectedCategory, sourceFilter, search]);

  // Group settings by category
  const groupedSettings = useMemo(() => {
    const map = new Map<string, typeof enrichedSettings>();
    filtered.forEach((item) => {
      const cat = item.computedCategory;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(item);
    });
    return Array.from(map.entries()).map(([category, items]) => ({
      category,
      items,
    }));
  }, [filtered]);

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const toggleCollapseAll = () => {
    if (collapsedCategories.size === groupedSettings.length) {
      setCollapsedCategories(new Set());
    } else {
      setCollapsedCategories(new Set(groupedSettings.map((g) => g.category)));
    }
  };

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const exportJSON = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(enrichedSettings, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', `configuration_${isOracle ? 'oracle_' : ''}${activeCluster || 'default'}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  const exportCSV = () => {
    const headers = ['Category', 'Parameter', 'Value', 'Unit', 'Source', 'Collected At', 'Description'];
    const rows = enrichedSettings.map((s) => [
      `"${s.computedCategory}"`,
      `"${s.name}"`,
      `"${String(s.setting ?? '').replace(/"/g, '""')}"`,
      `"${s.unit || ''}"`,
      `"${s.source || (s.isCustom ? 'custom' : 'default')}"`,
      `"${s.collected_at || ''}"`,
      `"${(s.short_desc || '').replace(/"/g, '""')}"`,
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', encodedUri);
    downloadAnchor.setAttribute('download', `configuration_${isOracle ? 'oracle_' : ''}${activeCluster || 'default'}.csv`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  if (loading) {
    return (
      <div className="bg-white/60 dark:bg-gray-900/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Loading database configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 relative">
      {/* Top Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
            <SlidersHorizontal className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">Total Parameters</div>
            <div className="text-base font-bold text-gray-900 dark:text-white font-mono">{summaryCounts.total}</div>
          </div>
        </div>

        <div
          onClick={() => setSourceFilter(sourceFilter === 'custom' ? 'all' : 'custom')}
          className={`bg-white dark:bg-gray-900 border rounded-xl p-3 shadow-sm flex items-center gap-3 cursor-pointer transition-all ${sourceFilter === 'custom'
              ? 'border-amber-500 ring-1 ring-amber-500/40 bg-amber-50/20 dark:bg-amber-950/20'
              : 'border-gray-200 dark:border-gray-800 hover:border-amber-500/40'
            }`}
          title="Click to filter by custom/modified parameters"
        >
          <div className="p-2 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-amber-600 dark:text-amber-400 flex items-center gap-1">
              Custom / Modified
            </div>
            <div className="text-base font-bold text-amber-600 dark:text-amber-400 font-mono">{summaryCounts.custom}</div>
          </div>
        </div>

        <div
          onClick={() => setSourceFilter(sourceFilter === 'default' ? 'all' : 'default')}
          className={`bg-white dark:bg-gray-900 border rounded-xl p-3 shadow-sm flex items-center gap-3 cursor-pointer transition-all ${sourceFilter === 'default'
              ? 'border-blue-500 ring-1 ring-blue-500/40 bg-blue-50/20 dark:bg-blue-950/20'
              : 'border-gray-200 dark:border-gray-800 hover:border-blue-500/40'
            }`}
          title="Click to filter by default parameters"
        >
          <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
            <Database className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">Default Settings</div>
            <div className="text-base font-bold text-gray-900 dark:text-white font-mono">{summaryCounts.defaultCount}</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20">
            <Layers className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">Categories</div>
            <div className="text-base font-bold text-gray-900 dark:text-white font-mono">{categoryStats.length}</div>
          </div>
        </div>
      </div>

      {/* Main Glassmorphism Container */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-2xl backdrop-blur-xl space-y-3">
        {/* Top Header & Search Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-800 pb-2.5">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-sm">
              <Wrench className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide">
                  {isOracle ? 'Oracle Database Configuration' : 'Runtime Configuration Parameters'}
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/30">
                  {filtered.length} / {settings.length}
                </span>
                {isOracle && (
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20">
                    Oracle SPFILE / Memory
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Search Input */}
            <div className="relative w-full sm:w-60">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search parameter or description..."
                className="w-full bg-gray-50 dark:bg-gray-950/80 border border-gray-200 dark:border-gray-700/80 rounded-lg pl-8 pr-3 py-1 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors h-7"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Source Filter Dropdown */}
            <CustomDropdown
              data={sourceOptions}
              selectedValue={sourceFilter}
              onSelect={(val) => setSourceFilter(val as 'all' | 'custom' | 'default')}
              width="w-44 sm:w-52"
              placeholder="All Sources"
              buttonHeight={8}
            />

            {/* Expand / Collapse All */}
            {groupedSettings.length > 0 && (
              <button
                onClick={toggleCollapseAll}
                className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 dark:bg-gray-800/80 hover:bg-gray-200 dark:hover:bg-gray-700/80 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700/70 text-xs font-semibold transition-all shrink-0 h-7 cursor-pointer"
                title="Expand/Collapse All Categories"
              >
                <ChevronsUpDown className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                <span className="hidden sm:inline">
                  {collapsedCategories.size === groupedSettings.length ? 'Expand All' : 'Collapse All'}
                </span>
              </button>
            )}

            {/* Export Actions */}
            <button
              onClick={exportCSV}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-800/80 hover:bg-gray-200 dark:hover:bg-gray-700/80 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700/70 text-xs font-semibold transition-all shrink-0 shadow-sm h-7 cursor-pointer"
              title="Export configuration as CSV"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-500" />
              <span className="hidden sm:inline">CSV</span>
            </button>

            <button
              onClick={exportJSON}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-800/80 hover:bg-gray-200 dark:hover:bg-gray-700/80 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700/70 text-xs font-semibold transition-all shrink-0 shadow-sm h-7 cursor-pointer"
              title="Export configuration as JSON"
            >
              <FileCode className="w-3.5 h-3.5 text-orange-500" />
              <span className="hidden sm:inline">JSON</span>
            </button>
          </div>
        </div>

        {/* Category Pills Navigation */}
        <div className="flex items-center gap-1.5 overflow-x-auto custom-scroll pb-1">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${selectedCategory === 'all'
                ? 'bg-orange-500 text-white shadow-sm'
                : 'bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
          >
            <span>All Categories</span>
            <span
              className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${selectedCategory === 'all'
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
            >
              {enrichedSettings.length}
            </span>
          </button>

          {categoryStats.map((cat) => {
            const isSelected = selectedCategory === cat.name;
            return (
              <button
                key={cat.name}
                onClick={() => setSelectedCategory(isSelected ? 'all' : cat.name)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${isSelected
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
              >
                <span>{cat.name}</span>
                <span
                  className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${isSelected
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                >
                  {cat.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Compact Parameter Table with Collapsible Categories */}
        <div className="overflow-x-auto overflow-y-auto custom-scroll border border-gray-200 dark:border-gray-800 rounded-lg max-h-[calc(100vh-270px)]">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold whitespace-nowrap shadow-sm">
                <th className="py-2 px-3 w-64 bg-gray-50 dark:bg-gray-900">Parameter</th>
                <th className="py-2 px-3 w-44 bg-gray-50 dark:bg-gray-900">Value</th>
                <th className="py-2 px-3 w-28 bg-gray-50 dark:bg-gray-900">Source</th>
                <th className="py-2 px-3 w-36 whitespace-nowrap bg-gray-50 dark:bg-gray-900">Last Changed</th>
                <th className="py-2 px-3 bg-gray-50 dark:bg-gray-900">Description</th>
                <th className="py-2 px-3 w-16 text-center bg-gray-50 dark:bg-gray-900">Info</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800/40 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900/20">
              {groupedSettings.length > 0 ? (
                groupedSettings.map((group) => {
                  const isCollapsed = collapsedCategories.has(group.category);

                  return (
                    <React.Fragment key={group.category}>
                      {/* Category Header Row */}
                      <tr
                        onClick={() => toggleCategory(group.category)}
                        className="bg-gray-100/90 dark:bg-gray-800 hover:bg-gray-200/80 dark:hover:bg-gray-800/80 border-t border-b border-gray-200 dark:border-gray-800 cursor-pointer transition-colors select-none"
                      >
                        <td colSpan={6} className="py-1.5 px-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <ChevronRight
                                className={`w-3.5 h-3.5 text-orange-500 dark:text-orange-400 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-90'
                                  }`}
                              />
                              <span className="text-xs font-bold text-gray-900 dark:text-white tracking-wide">
                                {group.category}
                              </span>
                              <span className="px-1.5 py-0.2 rounded-full text-[10px] font-mono font-bold bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/30">
                                {group.items.length}
                              </span>
                            </div>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">
                              {isCollapsed ? 'Click to expand' : 'Click to collapse'}
                            </span>
                          </div>
                        </td>
                      </tr>

                      {/* Category Items */}
                      {!isCollapsed &&
                        group.items.map((s, idx) => {
                          const valStr = s.setting !== undefined && s.setting !== null ? String(s.setting).trim() : '';
                          const valLower = valStr.toLowerCase();
                          const isBool =
                            valLower === 'on' ||
                            valLower === 'off' ||
                            valLower === 'true' ||
                            valLower === 'false';
                          const isOn = valLower === 'on' || valLower === 'true';

                          return (
                            <tr
                              key={`${group.category}-${s.name}-${idx}`}
                              onClick={() => setSelectedItem(s)}
                              className="hover:bg-orange-500/5 dark:hover:bg-gray-800/40 transition-colors border-b border-gray-100 dark:border-gray-800/30 h-8 cursor-pointer group"
                            >
                              {/* Parameter Name with Copy Button */}
                              <td className="py-1 px-3 pl-7 whitespace-nowrap">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-mono font-bold text-orange-600 dark:text-orange-400 text-[11px]">
                                    {s.name}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopy(s.name, `param-${s.name}`);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-orange-500 p-0.5 transition-opacity"
                                    title="Copy parameter name"
                                  >
                                    {copiedKey === `param-${s.name}` ? (
                                      <Check className="w-3 h-3 text-emerald-500" />
                                    ) : (
                                      <Copy className="w-3 h-3" />
                                    )}
                                  </button>
                                </div>
                              </td>

                              {/* Value with unit */}
                              <td className="py-1 px-3 whitespace-nowrap max-w-[180px]">
                                {isBool ? (
                                  <span
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-mono font-bold border ${isOn
                                        ? 'bg-emerald-100 dark:bg-green-500/10 text-emerald-700 dark:text-green-400 border-emerald-200 dark:border-green-500/30'
                                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                                      }`}
                                  >
                                    {isOn ? (
                                      <CheckCircle2 className="w-3 h-3 text-emerald-600 dark:text-green-400" />
                                    ) : (
                                      <XCircle className="w-3 h-3 text-gray-400" />
                                    )}
                                    {valStr.toUpperCase()}
                                  </span>
                                ) : (
                                  <div
                                    className="flex items-center gap-1 font-mono max-w-[170px] truncate"
                                    title={s.unit ? `${valStr} ${s.unit}` : valStr}
                                  >
                                    <span
                                      className={`text-[11px] font-bold truncate ${valStr === ''
                                          ? 'text-gray-400 italic'
                                          : 'text-gray-900 dark:text-white'
                                        }`}
                                    >
                                      {valStr === '' ? '(empty)' : valStr}
                                    </span>
                                    {s.unit && (
                                      <span className="text-[9px] text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-950 px-1 py-0.2 rounded border border-gray-200 dark:border-gray-800 font-mono shrink-0">
                                        {s.unit}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>

                              {/* Source */}
                              <td className="py-1 px-3 font-mono text-[10px] whitespace-nowrap">
                                {s.isCustom ? (
                                  <span className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded font-semibold text-[9px] uppercase tracking-wider">
                                    {s.source || 'CUSTOM'}
                                  </span>
                                ) : (
                                  <span className="bg-gray-100 dark:bg-gray-950 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-800 px-1.5 py-0.2 rounded">
                                    default
                                  </span>
                                )}
                              </td>

                              {/* Last Changed / Collected At */}
                              <td className="py-1 px-3 font-mono text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                {s.collected_at ? (
                                  <span className="text-gray-600 dark:text-gray-400" title={s.collected_at}>
                                    {new Date(s.collected_at).toLocaleTimeString([], {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      second: '2-digit',
                                    })}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </td>

                              {/* Description */}
                              <td
                                className="py-1 px-3 text-gray-600 dark:text-gray-300 text-[11px] max-w-md truncate"
                                title={s.short_desc}
                              >
                                {s.short_desc || '-'}
                              </td>

                              {/* Info Action */}
                              <td className="py-1 px-3 text-center">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedItem(s);
                                  }}
                                  className="text-gray-400 hover:text-orange-500 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                  title="View details"
                                >
                                  <Info className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-gray-500 dark:text-gray-400 italic">
                    No configuration parameters found matching filter criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Parameter Details Slide-over / Modal */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-950/50">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
                  <Wrench className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                    {selectedItem.name}
                  </h4>
                  <span className="text-[10px] text-gray-500 font-mono">
                    {getSmartCategory(selectedItem, isOracle)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedItem(null)}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto custom-scroll text-xs">
              {/* Setting Value */}
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1 flex items-center justify-between">
                  <span>Current Setting</span>
                  <button
                    onClick={() => handleCopy(String(selectedItem.setting ?? ''), 'modal-val')}
                    className="text-orange-500 hover:text-orange-600 flex items-center gap-1 text-[10px] font-mono cursor-pointer"
                  >
                    {copiedKey === 'modal-val' ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                    <span>{copiedKey === 'modal-val' ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
                <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 font-mono font-bold text-gray-900 dark:text-white break-all select-all">
                  {selectedItem.setting !== undefined && selectedItem.setting !== null && String(selectedItem.setting).trim() !== ''
                    ? String(selectedItem.setting)
                    : '(empty / unset)'}
                  {selectedItem.unit ? ` ${selectedItem.unit}` : ''}
                </div>
              </div>

              {/* Description */}
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                  Description
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed bg-gray-50 dark:bg-gray-950/50 p-2.5 rounded-lg border border-gray-200 dark:border-gray-800/80">
                  {selectedItem.short_desc || 'No description provided for this parameter.'}
                </p>
              </div>

              {/* Parameter Meta Grid */}
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100 dark:border-gray-800 text-[11px]">
                <div>
                  <span className="text-gray-500 block text-[10px]">Source:</span>
                  <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                    {selectedItem.source || 'default'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block text-[10px]">Database Type:</span>
                  <span className="font-mono font-semibold text-gray-800 dark:text-gray-200 uppercase">
                    {selectedItem.db_type || (isOracle ? 'oracle' : 'unknown')}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block text-[10px]">Category:</span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {getSmartCategory(selectedItem, isOracle)}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-950/50">
              <button
                onClick={() => setSelectedItem(null)}
                className="px-3 py-1.5 rounded-lg bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 font-semibold text-xs transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
